import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useCampaign, useAcceptCampaign, useDeclineCampaign } from "@/hooks/useCampaigns";
import { useCompany } from "@/hooks/useCompanies";
import { toast } from "sonner";

export default function CampaignAccept() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: campaign, isLoading } = useCampaign(id);
  const { data: company } = useCompany(campaign?.company_id?.toString());
  const accept = useAcceptCampaign();
  const decline = useDeclineCampaign();

  function handleAccept() {
    accept.mutate(id!, {
      onSuccess: () => {
        toast.success("Campanha aceita! Seu QR code já está disponível.");
        navigate("/campanhas");
      },
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Erro ao aceitar campanha";
        toast.error(msg);
      },
    });
  }

  function handleDecline() {
    if (!confirm("Tem certeza que deseja recusar esta campanha?")) return;
    decline.mutate(id!, {
      onSuccess: () => {
        toast.info("Campanha recusada.");
        navigate("/conversoes");
      },
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Erro ao recusar campanha";
        toast.error(msg);
      },
    });
  }

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-10">
        <h2 className="text-xl font-semibold">Campanha não encontrada</h2>
      </div>
    );
  }

  if (campaign.status !== "aguardando_aceite") {
    return (
      <div className="max-w-md mx-auto mt-16 text-center space-y-4">
        <div className="p-4 bg-muted rounded-xl inline-block">
          <Megaphone className="h-8 w-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-semibold">
          {campaign.status === "ativa" ? "Campanha já aceita" : "Campanha não está disponível"}
        </h2>
        <p className="text-sm text-muted-foreground">
          Esta campanha está com status <strong>{campaign.status}</strong>.
        </p>
        <Button variant="outline" onClick={() => navigate("/campanhas")}>Ver minhas campanhas</Button>
      </div>
    );
  }

  const fmtValor = (v: string | number) => {
    if (campaign.usa_pontos) return `${Math.round(Number(v))} pts`;
    return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  return (
    <div className="max-w-lg mx-auto space-y-6 pt-4">
      <div className="text-center space-y-2">
        <div className="p-3 bg-primary/10 rounded-xl text-primary inline-block">
          <Megaphone className="h-7 w-7" />
        </div>
        <h1 className="text-2xl font-bold">Convite de Campanha</h1>
        <p className="text-sm text-muted-foreground">
          Você foi convidado para participar da campanha abaixo.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl">{campaign.name}</CardTitle>
            <Badge variant="secondary">Aguardando aceite</Badge>
          </div>
          <CardDescription>{company?.name ?? "Carregando empresa..."}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">Remuneração</p>
              <p className="text-sm font-medium">
                {campaign.remuneracao_modelo === "percentual"
                  ? `${campaign.remuneracao_valor}% por venda`
                  : `${fmtValor(campaign.remuneracao_valor)} por venda`}
              </p>
            </div>
            {campaign.desconto_pct && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">Desconto ao Cliente</p>
                <p className="text-sm font-medium">{campaign.desconto_pct}%</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1 gap-2 border-destructive text-destructive hover:bg-destructive/10"
          onClick={handleDecline}
          disabled={decline.isPending || accept.isPending}
        >
          {decline.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
          Recusar
        </Button>
        <Button
          className="flex-1 gap-2"
          onClick={handleAccept}
          disabled={accept.isPending || decline.isPending}
        >
          {accept.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
          Aceitar Campanha
        </Button>
      </div>
    </div>
  );
}
